import { z } from 'zod';

import { QuestionFlowSchema } from '../questions/question-flow/question-flow';

/** How far one flow is from done, for this member specifically. */
export const FlowCompletionSchema = z
  .object({
    flow: QuestionFlowSchema,
    /** Required, active, in-audience questions of this flow. */
    requiredTotal: z.number().int().min(0),
    requiredAnswered: z.number().int().min(0),
    /**
     * Keys the member still owes, in form order — so a client can send them
     * straight back to the right page instead of making them hunt.
     */
    missingQuestionKeys: z.array(z.string()),
    isComplete: z.boolean(),
  })
  .strict();

export type FlowCompletion = z.infer<typeof FlowCompletionSchema>;

/**
 * The member's overall profile progress.
 *
 * `completionPct` counts **every** active in-audience question, required or
 * not — it is a nudge ("your profile is 60% complete"), and a bar that hits 100%
 * while half the optional questions are blank would stop nudging exactly when it
 * still could. `isComplete` per flow is the different, harder question, and it
 * counts required questions only, because that is what gates the lifecycle.
 */
export const ProfileCompletionSchema = z
  .object({
    completionPct: z.number().int().min(0).max(100),
    answeredTotal: z.number().int().min(0),
    questionTotal: z.number().int().min(0),
    flows: z.array(FlowCompletionSchema),
    /** Set once the wizard's required questions are all answered; never unset. */
    wizardCompletedAt: z.string().nullable(),
  })
  .strict();

export type ProfileCompletion = z.infer<typeof ProfileCompletionSchema>;
